import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMonitorsList, type MonitorListItem } from "@/monitors/api";
import type { MonitorStatus } from "@/monitors/schemas";

export function MonitorsListPage() {
  const { t } = useTranslation("monitors");
  const { data, isLoading, isError } = useMonitorsList();
  const [tab, setTab] = useState<MonitorStatus>("active");

  const monitors = data?.monitors ?? [];
  const grouped: Record<MonitorStatus, MonitorListItem[]> = {
    active: monitors.filter((m) => m.status === "active"),
    paused: monitors.filter((m) => m.status === "paused"),
    archived: monitors.filter((m) => m.status === "archived"),
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">{t("list.title")}</h1>
        <Button asChild>
          <Link to="/monitors/new">{t("list.createButton")}</Link>
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground">{t("list.loading")}</p>}
      {isError && <p className="text-red-600">{t("list.loadError")}</p>}

      {!isLoading && monitors.length === 0 && (
        <div className="text-center p-12">
          <p className="text-lg mb-4">{t("list.empty")}</p>
          <Button asChild>
            <Link to="/monitors/new">{t("list.createFirst")}</Link>
          </Button>
        </div>
      )}

      {monitors.length > 0 && (
        <Tabs value={tab} onValueChange={(v) => setTab(v as MonitorStatus)}>
          <TabsList>
            <TabsTrigger value="active">
              {t("list.tab.active", { count: grouped.active.length })}
            </TabsTrigger>
            <TabsTrigger value="paused">
              {t("list.tab.paused", { count: grouped.paused.length })}
            </TabsTrigger>
            <TabsTrigger value="archived">
              {t("list.tab.archived", { count: grouped.archived.length })}
            </TabsTrigger>
          </TabsList>
          {(["active", "paused", "archived"] as const).map((status) => (
            <TabsContent key={status} value={status} className="space-y-2 mt-4">
              {grouped[status].length === 0 && (
                <p className="text-muted-foreground">{t("list.tabEmpty")}</p>
              )}
              {grouped[status].map((m) => (
                <MonitorRow key={m.id} monitor={m} />
              ))}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function MonitorRow({ monitor }: { monitor: MonitorListItem }) {
  const { t } = useTranslation("monitors");
  return (
    <Link to={`/monitors/${monitor.id}`} className="block">
      <div className="border rounded-md p-3 hover:bg-muted/50 transition-colors">
        <div className="font-semibold">{monitor.canonical_statement}</div>
        {monitor.statement !== monitor.canonical_statement && (
          <div className="text-xs text-muted-foreground mt-1 truncate">{monitor.statement}</div>
        )}
        <div className="text-xs text-muted-foreground mt-2">
          {monitor.last_fired_at
            ? t("list.lastFired", {
                value: new Date(monitor.last_fired_at).toLocaleString(),
              })
            : t("list.neverFired")}
        </div>
      </div>
    </Link>
  );
}
